import { useMemo, useState } from 'react';

import { Divider, Typography, Stack, tokens } from '@cast/design-system';
import {
  WorkloadOptimizationHorizontalPodAutoscalerBehavior,
  WorkloadOptimizationMetricSpec,
} from '@cast/types';

import { AUTOSCALER_BEHAVIOR_POLICY_TYPE_NAMES } from 'features/cluster/workload-optimization/_constants';

import { getMetricsData, Metrics } from './_utils';
import { NativeSettingsInput } from './NativeSettingsInput';
import { SettingRow } from '../../../../../../_woop-settings/_components/layout/SettingRow';
import { SettingsForm } from '../../../../../../_woop-settings/_components/layout/SettingsForm';
import { SettingsLayout } from '../../../../../../_woop-settings/_components/layout/SettingsLayout';

const MetricsSettingsRow = ({
  title,
  name,
  description,
  selectedSettingName,
  setSelectedSettingName,
}: {
  name: MetricsSettings;
  title: string;
  description: string | string[];
  selectedSettingName: MetricsSettings;
  setSelectedSettingName: (name: MetricsSettings) => void;
}) => {
  const isSelected = selectedSettingName === name;

  return (
    <SettingRow
      testId={`setting-row-${name}`}
      title={title}
      description={description}
      isSelected={isSelected}
      onClick={() => setSelectedSettingName(name)}
    />
  );
};

const MetricsSettingsContent = ({
  metrics,
  minReplicas,
  maxReplicas,
  selectedSettingName,
  behavior,
}: {
  metrics: Metrics;
  selectedSettingName: MetricsSettings;
  minReplicas: string;
  maxReplicas: string;
  behavior?: WorkloadOptimizationHorizontalPodAutoscalerBehavior;
}) => {
  if (selectedSettingName === 'behavior') {
    return (
      <SettingsForm title="Behavior" contentWidth={360}>
        <Stack gap={24}>
          <Stack direction="row" gap={8}>
            <NativeSettingsInput
              value={
                behavior?.scaleUp?.stabilizationWindowSeconds != null
                  ? `${behavior.scaleUp.stabilizationWindowSeconds}sec`
                  : null
              }
              testId="native-scale-up-stabilization"
              fullWidth
              size="medium"
              label={
                <Typography
                  variant="P12M"
                  sx={{ color: tokens.theme.fg.structural.secondary }}
                >
                  Scale Up Stabilization
                </Typography>
              }
            />
            <NativeSettingsInput
              value={
                behavior?.scaleDown?.stabilizationWindowSeconds != null
                  ? `${behavior.scaleDown.stabilizationWindowSeconds}sec`
                  : null
              }
              testId="native-scale-down-stabilization"
              fullWidth
              size="medium"
              label={
                <Typography
                  variant="P12M"
                  sx={{ color: tokens.theme.fg.structural.secondary }}
                >
                  Scale Down Stabilization
                </Typography>
              }
            />
          </Stack>

          {behavior?.scaleUp?.policies &&
            behavior.scaleUp.policies.length > 0 && (
              <Stack gap={8}>
                <Typography variant="h6">Scale Up Policies</Typography>
                <Stack
                  rowGap={8}
                  divider={
                    <Divider
                      color={tokens.theme.border.structural.neutralSubtle}
                    />
                  }
                >
                  {behavior.scaleUp.policies.map((policy, index) => (
                    <Stack direction="row" gap={8} key={index}>
                      <NativeSettingsInput
                        value={
                          AUTOSCALER_BEHAVIOR_POLICY_TYPE_NAMES[policy.type] ??
                          policy.type
                        }
                        testId={`native-scale-up-policy-${index}-type`}
                        fullWidth
                        size="medium"
                        label={
                          <Typography
                            variant="P12M"
                            sx={{ color: tokens.theme.fg.structural.secondary }}
                          >
                            Policy Type
                          </Typography>
                        }
                      />
                      <NativeSettingsInput
                        value={`${policy.value}`}
                        testId={`native-scale-up-policy-${index}-value`}
                        fullWidth
                        size="medium"
                        label={
                          <Typography
                            variant="P12M"
                            sx={{ color: tokens.theme.fg.structural.secondary }}
                          >
                            Value
                          </Typography>
                        }
                      />
                      <NativeSettingsInput
                        value={`${policy.periodSeconds}sec`}
                        testId={`native-scale-up-policy-${index}-period`}
                        fullWidth
                        size="medium"
                        label={
                          <Typography
                            variant="P12M"
                            sx={{ color: tokens.theme.fg.structural.secondary }}
                          >
                            Period
                          </Typography>
                        }
                      />
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            )}

          {behavior?.scaleDown?.policies &&
            behavior.scaleDown.policies.length > 0 && (
              <Stack gap={8}>
                <Typography variant="h6">Scale Down Policies</Typography>
                <Stack
                  rowGap={8}
                  divider={
                    <Divider
                      color={tokens.theme.border.structural.neutralSubtle}
                    />
                  }
                >
                  {behavior.scaleDown.policies.map((policy, index) => (
                    <Stack direction="row" gap={8} key={index}>
                      <NativeSettingsInput
                        value={
                          AUTOSCALER_BEHAVIOR_POLICY_TYPE_NAMES[policy.type] ??
                          policy.type
                        }
                        testId={`native-scale-down-policy-${index}-type`}
                        fullWidth
                        size="medium"
                        label={
                          <Typography
                            variant="P12M"
                            sx={{ color: tokens.theme.fg.structural.secondary }}
                          >
                            Policy Type
                          </Typography>
                        }
                      />
                      <NativeSettingsInput
                        value={`${policy.value}`}
                        testId={`native-scale-down-policy-${index}-value`}
                        fullWidth
                        size="medium"
                        label={
                          <Typography
                            variant="P12M"
                            sx={{ color: tokens.theme.fg.structural.secondary }}
                          >
                            Value
                          </Typography>
                        }
                      />
                      <NativeSettingsInput
                        value={`${policy.periodSeconds}sec`}
                        testId={`native-scale-down-policy-${index}-period`}
                        fullWidth
                        size="medium"
                        label={
                          <Typography
                            variant="P12M"
                            sx={{ color: tokens.theme.fg.structural.secondary }}
                          >
                            Period
                          </Typography>
                        }
                      />
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            )}
        </Stack>
      </SettingsForm>
    );
  }

  if (selectedSettingName === 'pod-count') {
    return (
      <SettingsForm title="Pod count" contentWidth={360}>
        <Stack direction="row" gap={8}>
          <NativeSettingsInput
            value={minReplicas ?? ''}
            testId="native-min-pods"
            fullWidth
            size="medium"
            label={
              <Typography
                variant="P12M"
                sx={{ color: tokens.theme.fg.structural.secondary }}
              >
                Min
              </Typography>
            }
          />
          <NativeSettingsInput
            value={maxReplicas ?? ''}
            testId="native-max-pods"
            fullWidth
            size="medium"
            label={
              <Typography
                variant="P12M"
                sx={{ color: tokens.theme.fg.structural.secondary }}
              >
                Max
              </Typography>
            }
          />
        </Stack>
      </SettingsForm>
    );
  }
  return (
    <SettingsForm
      title={metrics[selectedSettingName][0].title}
      contentWidth={360}
    >
      <Stack
        rowGap={24}
        divider={
          <Divider color={tokens.theme.border.structural.neutralSubtle} />
        }
      >
        {metrics[selectedSettingName].map(
          ({ inputLabel, label, name, description, value, isPercentage }) => (
            <Stack
              gap={8}
              key={`${name}-${label}-${description}-${inputLabel}`}
            >
              <Stack>
                <Typography variant="A1">{name}</Typography>
                {description && (
                  <Typography variant="P12R" mb={12}>
                    {description}
                  </Typography>
                )}
              </Stack>
              <NativeSettingsInput
                value={value}
                label={
                  <Typography
                    variant="P12M"
                    sx={{ color: tokens.theme.fg.structural.secondary }}
                  >
                    {inputLabel}
                  </Typography>
                }
                testId={`${name}-${label}-${inputLabel}`}
                endAdornment={isPercentage ? '%' : undefined}
                fullWidth={false}
                size="medium"
              />
            </Stack>
          )
        )}
      </Stack>
    </SettingsForm>
  );
};

type MetricsSettings = keyof Metrics | 'pod-count' | 'behavior';

export const MetricsSection = ({
  hpaMetrics,
  maxReplicas,
  minReplicas,
  behavior,
}: {
  hpaMetrics: WorkloadOptimizationMetricSpec[];
  minReplicas: string;
  maxReplicas: string;
  behavior?: WorkloadOptimizationHorizontalPodAutoscalerBehavior;
}) => {
  const metrics = useMemo(() => getMetricsData({ hpaMetrics }), [hpaMetrics]);
  const settingsList: MetricsSettings[] = [
    'pod-count' as const,
    ...(Object.keys(metrics) as (keyof Metrics)[]).filter(
      (key) => metrics[key].length > 0
    ),
    ...(behavior ? (['behavior'] as const) : []),
  ];

  const [selectedSettingName, setSelectedSettingName] =
    useState<MetricsSettings>('pod-count');

  const behaviorDescription = [
    behavior?.scaleUp?.stabilizationWindowSeconds != null
      ? `Scale up: ${behavior.scaleUp.stabilizationWindowSeconds}sec`
      : null,
    behavior?.scaleDown?.stabilizationWindowSeconds != null
      ? `Scale down: ${behavior.scaleDown.stabilizationWindowSeconds}sec`
      : null,
  ].filter(Boolean) as string[];

  return (
    <SettingsLayout
      settingsRows={settingsList.map((name) => {
        if (name === 'pod-count') {
          return (
            <MetricsSettingsRow
              title="Pod count"
              key={name}
              name={name}
              description={`Min: ${minReplicas}, Max: ${maxReplicas}`}
              setSelectedSettingName={setSelectedSettingName}
              selectedSettingName={selectedSettingName}
            />
          );
        }
        if (name === 'behavior') {
          return (
            <MetricsSettingsRow
              title="Behavior"
              key={name}
              name={name}
              description={
                behaviorDescription.length > 0
                  ? behaviorDescription
                  : 'Stabilization, Policies'
              }
              setSelectedSettingName={setSelectedSettingName}
              selectedSettingName={selectedSettingName}
            />
          );
        }
        return (
          <MetricsSettingsRow
            title={metrics[name][0].title}
            key={name}
            name={name}
            description={metrics[name].map((m) => m.name)}
            setSelectedSettingName={setSelectedSettingName}
            selectedSettingName={selectedSettingName}
          />
        );
      })}
      settingsContent={
        <MetricsSettingsContent
          metrics={metrics}
          maxReplicas={maxReplicas}
          minReplicas={minReplicas}
          selectedSettingName={selectedSettingName}
          behavior={behavior}
        />
      }
    />
  );
};
